import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse, CourseCategory, SkillLevel } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        // Construct base URL from apiUrl and apiVersion configured in
        // Constants.ts. This makes the API version configurable via
        // environment variables.
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        
        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

export interface Course {
    id: string
    title: string
    description: string
    full_price: number
    discount_price?: number
    category: CourseCategory
    is_active: boolean
    hidden: boolean
    duration_hours?: number
    skill_level?: SkillLevel
    thumbnail_url?: string
    created_at: string
    updated_at: string
}

export interface CourseListData {
    courses: Course[]
    total?: number
    page?: number
    page_size?: number
}

export interface CourseQueryParams {
    page?: number
    page_size?: number
    is_active?: boolean
    hidden?: boolean
}

export const courseService = {
    getAllCourses,
    getCourseById,
    createCourse,
    updateCourse,
    deleteCourse,
    toggleCourseStatus,
}

/**
 * Get all courses with optional filters
 */
async function getAllCourses(params?: CourseQueryParams): Promise<ApiResponse<CourseListData>> {
    try {
        const response = await getApiInstance().get('/courses', { params })
        const data = response.data
        
        // Handle wrapped response from backend (ResponseModel)
        let courses: Course[] = []
        let total: number | undefined
        let page: number | undefined
        let page_size: number | undefined
        
        if (data.success && data.data) {
            courses = data.data.courses || []
            total = data.data.total
            page = data.data.page
            page_size = data.data.page_size
        } else if (data.courses) {
            courses = data.courses
            total = data.total || data.courses.length
            page = data.page || 1
            page_size = data.page_size || data.courses.length
        } else if (data.data) {
            courses = data.data
        }
        
        return {
            success: true,
            data: { courses, total, page, page_size }
        }
    } catch (error: any) {
        console.error('Error fetching courses:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch courses'
        }
    }
}

/**
 * Get a single course by ID
 */
async function getCourseById(id: string): Promise<ApiResponse<Course>> {
    try {
        const response = await getApiInstance().get(`/courses/${id}`)
        const course = response.data?.data || response.data
        
        if (!course) {
            return {
                success: false,
                error: 'Course not found'
            }
        }
        
        return {
            success: true,
            data: course
        }
    } catch (error: any) {
        console.error('Error fetching course:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to fetch course'
        }
    }
}

/**
 * Create a new course
 */
async function createCourse(data: Partial<Course>): Promise<ApiResponse<Course>> {
    try {
        const response = await getApiInstance().post('/courses', data)
        const course = response.data?.data || response.data
        
        return {
            success: true,
            data: course
        }
    } catch (error: any) {
        console.error('Error creating course:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to create course'
        }
    }
}

/**
 * Update an existing course
 */
async function updateCourse(id: string, data: Partial<Course>): Promise<ApiResponse<Course>> {
    try {
        const response = await getApiInstance().put(`/courses/${id}`, data)
        const course = response.data?.data || response.data
        
        return {
            success: true,
            data: course
        }
    } catch (error: any) {
        console.error('Error updating course:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to update course'
        }
    }
}

/**
 * Delete a course
 */
async function deleteCourse(id: string): Promise<ApiResponse<void>> {
    try {
        await getApiInstance().delete(`/courses/${id}`)
        
        return {
            success: true,
            data: undefined
        }
    } catch (error: any) {
        console.error('Error deleting course:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to delete course'
        }
    }
}

/**
 * Toggle course active status
 */
async function toggleCourseStatus(id: string, isActive: boolean): Promise<ApiResponse<Course>> {
    try {
        const response = await getApiInstance().put(`/courses/${id}`, { is_active: isActive })
        const course = response.data?.data || response.data
        
        return {
            success: true,
            data: course
        }
    } catch (error: any) {
        console.error('Error toggling course status:', error)
        return {
            success: false,
            error: error.response?.data?.message || error.message || 'Failed to toggle course status'
        }
    }
}

// Re-export types for convenience
export type { CourseCategory, SkillLevel } from '../types/api.types'
